import { getAvailableScopeOptions } from "@app/components/assistant/conversation/interactive_content/frame/shareFrameScopeOptions";
import { useAwaitableDialog } from "@app/hooks/useAwaitableDialog";
import type {
  ShareFrameViewerFile,
  ShareFrameViewerFileSourceKind,
} from "@app/lib/api/viz/share_frame_viewer_files";
import { getFileTypeIcon } from "@app/lib/file_icon_utils";
import {
  useShareInteractiveContentFile,
  useSharingGrants,
} from "@app/lib/swr/files";
import { useWorkspacePermissions } from "@app/lib/swr/permissions";
import { useIsMobile } from "@app/lib/swr/useIsMobile";
import { isEmailValid } from "@app/lib/utils";
import {
  type FileShareScope,
  MAX_EMAILS_PER_INVITE,
  type SharingGrantType,
} from "@app/types/files";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Avatar,
  Button,
  Clipboard,
  ClipboardCheck,
  ContentMessage,
  ContextItem,
  Cube01,
  File02,
  Icon,
  IconButton,
  InfoCircle,
  Input,
  Label,
  MessageChatSquare,
  Sheet,
  SheetContainer,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Spinner,
  Upload01,
  useCopyToClipboard,
  XClose,
} from "@dust-tt/sparkle";
import { zodResolver } from "@hookform/resolvers/zod";
import { intlFormatDistance } from "date-fns";
import i18n from "i18next";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const baseScopeOptionClassNames =
  "flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors";

function getScopeOptionClassName({
  isSelected,
  isDisabled,
}: {
  isSelected: boolean;
  isDisabled: boolean;
}): string {
  const cursor = isDisabled
    ? "cursor-not-allowed opacity-60"
    : "cursor-pointer";
  const state = isSelected
    ? "border-highlight-300 bg-muted-background"
    : isDisabled
      ? "border-transparent"
      : "border-transparent hover:bg-muted-background/50";

  return `${baseScopeOptionClassNames} ${cursor} ${state}`;
}

const inviteFormSchema = z.object({
  emailsRaw: z
    .string()
    .min(1)
    .superRefine((val, ctx) => {
      const emails = val
        .split(",")
        .map((e) => e.trim())
        .filter((e) => e.length > 0);

      if (emails.length > MAX_EMAILS_PER_INVITE) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t("conv.shareMaxEmailsInvite", {
            defaultValue: "You can invite up to {{count}} people at a time",
            count: MAX_EMAILS_PER_INVITE,
          }),
        });
        return;
      }

      const invalid = emails.filter((e) => !isEmailValid(e));
      if (invalid.length > 0) {
        const quoted = invalid.map((e) => `"${e}"`).join(", ");
        const verb =
          invalid.length === 1
            ? i18n.t(
                "conv.shareEmailInvalidSingular",
                "is not a valid email address"
              )
            : i18n.t(
                "conv.shareEmailInvalidPlural",
                "are not valid email addresses"
              );
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${quoted} ${verb}`,
        });
      }
    }),
});

type InviteFormValues = z.infer<typeof inviteFormSchema>;

interface ShareFrameSheetProps {
  fileId: string;
  owner: LightWorkspaceType;
  /** Busts share/grants cache when frame content changes (e.g. `fileId@updatedAt`). */
  contentHash?: string | null;
}

export function ShareFrameSheet({
  fileId,
  owner,
  contentHash,
}: ShareFrameSheetProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [shareBlockError, setShareBlockError] = useState<string[] | null>(null);
  const [isCopied, copyToClipboard] = useCopyToClipboard();
  const { AwaitableDialog, showDialog } = useAwaitableDialog();

  const { doShare, fileShare, isFileShareLoading } =
    useShareInteractiveContentFile({
      fileId,
      owner,
      cacheKey: contentHash,
    });

  const { grants, isGrantsLoading, doAddGrants, doRevokeGrant } =
    useSharingGrants({
      fileId,
      owner,
      cacheKey: contentHash,
      disabled: !isOpen,
    });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const currentScope: FileShareScope =
    fileShare?.scope ?? "workspace_and_emails";
  const shareURL = fileShare?.shareUrl ?? "";
  const viewerFiles = fileShare?.viewerFiles ?? [];

  const externalSharingDisabledByPolicy =
    owner.sharingPolicy === "workspace_only";

  const { hasPermission } = useWorkspacePermissions();

  const canInviteExternal =
    !externalSharingDisabledByPolicy && hasPermission("invite", "frame");
  const canPublish =
    owner.sharingPolicy === "all_scopes" && hasPermission("publish", "frame");

  const lostPublishPermission = currentScope === "public" && !canPublish;

  const availableScopeOptions = getAvailableScopeOptions({
    sharingPolicy: owner.sharingPolicy,
    canInviteExternal,
    canPublish,
    currentScope,
  });

  const showEmailSection =
    currentScope === "emails_only" || currentScope === "workspace_and_emails";

  const activeGrants = grants
    .filter((g) => !g.blockedByPolicy)
    .sort((a, b) => a.email.localeCompare(b.email));

  const blockedGrants = grants
    .filter((g) => g.blockedByPolicy)
    .sort((a, b) => a.email.localeCompare(b.email));

  const onInviteSubmit = async (data: InviteFormValues) => {
    const emails = data.emailsRaw
      .split(",")
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    await doAddGrants(emails);
    reset();
  };

  const handleRevoke = async (grant: SharingGrantType) => {
    const confirmed = await showDialog({
      title: t("conv.shareRevokeAccessTitle", "Revoke access"),
      validateLabel: t("conv.shareRevoke", "Revoke"),
      validateVariant: "warning",
      cancelLabel: t("conv.shareCancel", "Cancel"),
      children: (
        <p>
          {t(
            "conv.shareRevokeAccessConfirm",
            "Are you sure you want to revoke access for {{email}}? They will no longer be able to view this frame.",
            { email: grant.email }
          )}
        </p>
      ),
    });

    if (confirmed) {
      await doRevokeGrant(grant.id);
    }
  };

  return (
    <>
      <AwaitableDialog />
      <Button
        variant="ghost"
        label={isMobile ? undefined : t("conv.share", "Share")}
        tooltip={isMobile ? t("conv.share", "Share") : undefined}
        icon={Upload01}
        onClick={() => setIsOpen(true)}
      />
      <Sheet
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) {
            setShareBlockError(null);
          }
        }}
      >
        <SheetContent size="lg">
          <SheetHeader>
            <div className="flex items-center justify-between pr-10">
              <SheetTitle>
                {t("conv.shareThisFrame", "Share this frame")}
              </SheetTitle>
              <Button
                variant="ghost"
                size="sm"
                icon={isCopied ? ClipboardCheck : Clipboard}
                label={
                  isCopied
                    ? t("conv.copied", "Copied!")
                    : t("conv.copyLink", "Copy link")
                }
                onClick={async () => {
                  await copyToClipboard(shareURL);
                }}
              />
            </div>
          </SheetHeader>
          <SheetContainer>
            {isFileShareLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="sm" />
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                {!canInviteExternal && (
                  <ContentMessage
                    icon={InfoCircle}
                    variant="info"
                    title={t(
                      "conv.shareOnlyWorkspaceMembersTitle",
                      "Only workspace members can be added"
                    )}
                  >
                    {externalSharingDisabledByPolicy
                      ? t(
                          "conv.shareExternalDisabledByAdmin",
                          "Your admin has disabled external sharing. You can only invite people already in your workspace."
                        )
                      : t(
                          "conv.shareNoExternalInvitePermission",
                          "You don’t have permission to invite people outside your workspace. You can only invite people already in your workspace."
                        )}
                  </ContentMessage>
                )}
                {lostPublishPermission && (
                  <ContentMessage
                    icon={InfoCircle}
                    variant="info"
                    title={t(
                      "conv.shareLostPublishPermissionTitle",
                      "You no longer have permission to share frames publicly"
                    )}
                  >
                    {t(
                      "conv.shareLostPublishPermissionBody",
                      "This frame is currently shared publicly. You can restrict access, but you won’t be able to make it public again."
                    )}
                  </ContentMessage>
                )}
                {shareBlockError && shareBlockError.length > 0 && (
                  <ContentMessage
                    icon={InfoCircle}
                    variant="warning"
                    title={t(
                      "conv.shareBlockedFilesTitle",
                      "Some referenced files cannot be shared"
                    )}
                  >
                    {t(
                      "conv.shareBlockedFilesBody",
                      "Viewers will only be able to access files you can verify. Fix or remove these references before sharing: {{refs}}",
                      { refs: shareBlockError.join(", ") }
                    )}
                  </ContentMessage>
                )}
                <fieldset className="flex flex-col gap-2 border-none p-0">
                  <legend className="text-sm font-semibold text-foreground mb-2">
                    {t("conv.shareWhoHasAccess", "Who has access")}
                  </legend>
                  <div className="flex flex-col gap-1">
                    {availableScopeOptions.map((option) => {
                      const isSelected = option.value === currentScope;
                      const isDisabled = option.disabled;
                      const inputId = `share-scope-${option.value}`;
                      return (
                        <Label
                          key={option.value}
                          htmlFor={inputId}
                          className={getScopeOptionClassName({
                            isSelected,
                            isDisabled,
                          })}
                        >
                          <input
                            type="radio"
                            id={inputId}
                            name="share-scope"
                            value={option.value}
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={async () => {
                              setShareBlockError(null);
                              const result = await doShare(option.value);
                              if (
                                !result.success &&
                                result.unverifiableRefs?.length
                              ) {
                                setShareBlockError(result.unverifiableRefs);
                              }
                            }}
                            className="sr-only"
                          />
                          <Icon
                            visual={option.icon}
                            size="sm"
                            className="shrink-0 text-muted-foreground"
                          />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-primary">
                              {option.label}
                            </span>
                            <span className="copy-xs text-muted-foreground">
                              {option.description}
                            </span>
                          </div>
                        </Label>
                      );
                    })}
                  </div>
                </fieldset>

                {showEmailSection && (
                  <div className="flex flex-col gap-4">
                    <form
                      className="flex flex-col gap-2"
                      onSubmit={handleSubmit(onInviteSubmit)}
                    >
                      <Label htmlFor="email-invite">
                        {!canInviteExternal
                          ? t(
                              "conv.shareInviteWorkspaceMembersByEmail",
                              "Invite workspace members by email"
                            )
                          : t("conv.shareInviteByEmail", "Invite by email")}
                      </Label>
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <Input
                            id="email-invite"
                            placeholder={t(
                              "conv.shareInviteEmailPlaceholder",
                              "Add comma separated emails to invite"
                            )}
                            {...register("emailsRaw")}
                            message={errors.emailsRaw?.message}
                            messageStatus={
                              errors.emailsRaw ? "error" : undefined
                            }
                          />
                        </div>
                        <Button
                          variant="primary"
                          label={t("conv.shareInvite", "Invite")}
                          type="submit"
                          disabled={isSubmitting}
                        />
                      </div>
                    </form>

                    {isGrantsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Spinner size="sm" />
                      </div>
                    ) : grants.length === 0 ? (
                      <p className="py-2 text-center text-sm text-muted-foreground">
                        {t(
                          "conv.shareNoOneInvitedYet",
                          "No one has been invited yet."
                        )}
                      </p>
                    ) : (
                      <div className="flex flex-col">
                        {activeGrants.map((grant) => (
                          <GrantRow
                            key={grant.id}
                            grant={grant}
                            onRevoke={() => handleRevoke(grant)}
                          />
                        ))}
                        {blockedGrants.length > 0 && (
                          <>
                            <p className="pb-2 pt-6 text-xs font-medium text-warning-500">
                              {t(
                                "conv.shareNoLongerHasAccess",
                                "No longer have access since they are not in your workspace"
                              )}
                            </p>
                            {blockedGrants.map((grant) => (
                              <GrantRow
                                key={grant.id}
                                grant={grant}
                                onRevoke={() => handleRevoke(grant)}
                                blocked
                              />
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <ViewerFilesSection viewerFiles={viewerFiles} />
              </div>
            )}
          </SheetContainer>
        </SheetContent>
      </Sheet>
    </>
  );
}

const VIEWER_FILE_SOURCE_ICONS: Record<
  ShareFrameViewerFileSourceKind,
  typeof Cube01
> = {
  pod: Cube01,
  conversation: MessageChatSquare,
  workspace: File02,
};

function ViewerFileLine({ viewerFile }: { viewerFile: ShareFrameViewerFile }) {
  const { t } = useTranslation();
  const SourceIcon = VIEWER_FILE_SOURCE_ICONS[viewerFile.sourceKind];
  const FileIcon = getFileTypeIcon(viewerFile.contentType, viewerFile.name);

  return (
    <li className="flex min-w-0 items-center gap-1.5 py-0.5 text-xs text-foreground">
      <Icon
        visual={FileIcon}
        size="xs"
        className="shrink-0 text-muted-foreground"
      />
      <div className="flex min-w-0 items-center gap-1 truncate">
        <span className="shrink-0 font-medium">{viewerFile.name}</span>
        <span className="shrink-0 text-muted-foreground">
          {t("conv.shareFrom", "from")}
        </span>
        <Icon
          visual={SourceIcon}
          size="xs"
          className="shrink-0 text-muted-foreground"
        />
        <span className="truncate">{viewerFile.sourceName}</span>
        {viewerFile.pathInSource ? (
          <span className="truncate text-muted-foreground">
            {t("conv.shareInPath", "in /{{path}}", {
              path: viewerFile.pathInSource,
            })}
          </span>
        ) : null}
      </div>
    </li>
  );
}

function ViewerFilesSection({
  viewerFiles,
}: {
  viewerFiles: ShareFrameViewerFile[];
}) {
  const { t } = useTranslation();
  if (viewerFiles.length === 0) {
    return null;
  }

  return (
    <fieldset className="flex flex-col gap-2 border-none p-0">
      <legend className="text-sm font-semibold text-foreground">
        {t("conv.shareFilesUsed", "Files used")}
      </legend>
      <p className="copy-xs text-muted-foreground">
        {t(
          "conv.shareFilesUsedDescription",
          "When shared, viewers can only access these files—not the rest of the conversation or pod."
        )}
      </p>
      <ul className="flex flex-col gap-0">
        {viewerFiles.map((viewerFile, index) => (
          <ViewerFileLine
            key={`${viewerFile.sourceKind}:${viewerFile.sourceName}:${viewerFile.name}:${index}`}
            viewerFile={viewerFile}
          />
        ))}
      </ul>
    </fieldset>
  );
}

interface GrantRowProps {
  grant: SharingGrantType;
  onRevoke: () => void;
  blocked?: boolean;
}

function GrantRow({ grant, onRevoke, blocked = false }: GrantRowProps) {
  const { t } = useTranslation();
  const now = new Date();
  const invitedBy = grant.grantedBy?.fullName ?? grant.grantedBy?.email;
  const grantedAgo = intlFormatDistance(new Date(grant.grantedAt), now);
  const invitedLabel = invitedBy
    ? t("conv.shareInvitedByAgo", "Invited by {{invitedBy}} {{grantedAgo}}", {
        invitedBy,
        grantedAgo,
      })
    : t("conv.shareInvitedAgo", "Invited {{grantedAgo}}", { grantedAgo });

  const viewedLabel = grant.lastViewedAt
    ? t("conv.shareViewedAgo", "Viewed {{viewedAgo}}", {
        viewedAgo: intlFormatDistance(new Date(grant.lastViewedAt), now),
      })
    : t("conv.shareNeverViewed", "Never viewed");

  // TODO(sparkle): ContextItem forces items-start when children are present.
  // Add an itemsAlignment prop to ContextItem to allow centering.
  return (
    <ContextItem
      title={grant.email}
      visual={
        <Avatar
          size="xs"
          name={grant.email}
          isRounded
          className={blocked ? "opacity-40" : undefined}
        />
      }
      hasSeparator={false}
      action={
        <IconButton
          icon={XClose}
          tooltip={t("conv.shareRevokeAccessTitle", "Revoke access")}
          size="xs"
          onClick={onRevoke}
        />
      }
      hoverAction
      className={blocked ? "opacity-60" : undefined}
    >
      <span className="text-xs text-muted-foreground">
        {invitedLabel} · {viewedLabel}
      </span>
    </ContextItem>
  );
}
